/**
 * The headless world stepper — the heart of the deterministic sim. The SAME code
 * runs in three places (docs/BARRAGE_IMPLEMENTATION_PLAN.md, "one sim, three jobs"):
 *  - the browser (Phaser drives `stepWorld` from its rAF loop, then renders),
 *  - a Cloud Function (re-runs a submitted {@link RunRecord} to compute the
 *    authoritative score → anti-cheat), and
 *  - replays (re-runs stored runs for playback).
 *
 * Hard rules (so all three agree byte-for-byte): NO `Math.random` (draw from
 * `world.rng`), NO wall-clock time (`Date.now`/`performance.now`), and NO Phaser/
 * React/Firebase imports. Time advances ONLY via the `dtMs` the caller passes.
 */
import type { GameMode, Hazard, PlayerInput, RectHazard, RunInputSample, RunRecord, Viewport, WorldState } from './types';
import {
  PLAYER,
  SIM,
  WALL,
  TURRET_BODY,
  CARO,
  SOLO_ATTACK_BELOW_STEP,
  ATTACK_GAP_MS,
} from './constants';
import { createRng } from './rng';
import { difficultyForElapsed, speedStepForElapsed } from './difficulty';
import { scoreFromElapsedMs } from './simulation';
import { selectPattern } from './patterns';
import {
  circleIntersectsCircle,
  circleIntersectsRect,
  circleIntersectsRotatedBeam,
  circleIntersectsSegment,
} from './collisionGeo';

/** Two full turns — the spacing of evenly distributed caro beams uses τ. */
const TWO_PI = Math.PI * 2;

/**
 * Build a fresh, deterministic world for a run. The world's `rng` is seeded from
 * `seed` (via {@link createRng}) so two worlds built from the same seed evolve
 * identically given the same inputs. Player starts centred in `viewport`.
 *
 * @param seed     the run seed (also recorded in the {@link RunRecord}).
 * @param viewport the play area to simulate in (px).
 * @param mode     solo or multiplayer (multiplayer shares one seed across players).
 */
export function createWorld(seed: number, viewport: Viewport, mode: GameMode): WorldState {
  return {
    elapsedMs: 0,
    playerX: viewport.width / 2,
    playerY: viewport.height / 2,
    playerRadius: PLAYER.radius,
    hazards: [],
    rng: createRng(seed),
    seed,
    nextAttackAtMs: 0,
    lastFamily: null,
    nextHazardId: 1,
    alive: true,
    deathFamily: null,
    mode,
    viewport,
  };
}

/**
 * The frame-rate-independent easing factor for one step of length `dtMs`.
 *
 * {@link PLAYER.followLerp} is tuned for the 60 Hz fixed step; in production
 * `dtMs` is always {@link SIM.fixedDeltaMs} so this returns `PLAYER.followLerp`
 * unchanged. Should a caller ever step with a different `dtMs` we rescale via the
 * standard exponential-decay correction so the ball reaches the target at the
 * same real rate — keeping behaviour stable (and, given a fixed `dtMs`,
 * deterministic).
 */
function followFactor(dtMs: number): number {
  if (dtMs === SIM.fixedDeltaMs) return PLAYER.followLerp;
  const steps = dtMs / SIM.fixedDeltaMs;
  // 1 - (1 - k)^steps is the compounded fraction covered over `steps` ticks.
  return 1 - Math.pow(1 - PLAYER.followLerp, steps);
}

/** Clamp `v` into `[lo, hi]` (assumes `lo <= hi`). */
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Has this hazard finished telegraphing (i.e. entered its lethal window)? */
function isActive(h: Hazard, elapsedMs: number): boolean {
  const activeStart = h.bornMs + h.telegraphMs;
  return elapsedMs >= activeStart && elapsedMs < activeStart + h.activeMs;
}

/** Smoothstep ease (slow-in, slow-out) for `p` in [0,1]. */
function smooth(p: number): number {
  const c = p < 0 ? 0 : p > 1 ? 1 : p;
  return c * c * (3 - 2 * c);
}

/**
 * The animated coordinate of a half-screen crusher wall at `elapsedMs`, computed
 * directly from its `restPos`/`creepPos`/`crushPos` keyframes (a pure function of
 * time — no easing accumulation, so it's bit-identical client/server). The caller
 * applies the result to `h.x` or `h.y` per `crushAxis`. Phases:
 *  - CREEP  (telegraph): `restPos` → `creepPos`, slow (smoothstep).
 *  - CRUSH  (first {@link WALL.crushFraction} of active): `creepPos` → `crushPos`, a
 *    fast snap (ease-out so it lunges then settles).
 *  - HOLD   (next {@link WALL.holdFraction} of active): stays at `crushPos` (lethal).
 *  - OPEN   (remainder of active): `crushPos` → `restPos` (retract, smoothstep).
 */
function crusherPos(h: RectHazard, elapsedMs: number): number {
  const restPos = h.restPos!;
  const creepPos = h.creepPos!;
  const crushPos = h.crushPos!;
  const t = elapsedMs - h.bornMs;

  if (t < h.telegraphMs) {
    const p = h.telegraphMs > 0 ? t / h.telegraphMs : 1;
    return restPos + (creepPos - restPos) * smooth(p);
  }

  const at = t - h.telegraphMs; // age within the active window
  const crushMs = h.activeMs * WALL.crushFraction;
  const holdMs = h.activeMs * WALL.holdFraction;

  if (at < crushMs) {
    const p = crushMs > 0 ? at / crushMs : 1;
    const snap = 1 - (1 - p) * (1 - p); // ease-out: fast lunge, decelerate
    return creepPos + (crushPos - creepPos) * snap;
  }
  if (at < crushMs + holdMs) {
    return crushPos;
  }
  const openMs = Math.max(1, h.activeMs - crushMs - holdMs);
  const p = Math.min(1, (at - crushMs - holdMs) / openMs);
  return crushPos + (restPos - crushPos) * smooth(p);
}

/**
 * Is a BLINKING segment lethal right now? A segment with `blinkPeriodMs` set is only
 * lethal for `blinkDutyOn` ms out of every period (measured from its active start);
 * any other hazard (or a segment without blink) is always lethal while active. Pure
 * + deterministic (a function of elapsed time), so the renderer and the collision
 * test agree and replays reproduce exactly.
 */
export function segmentBlinkOn(h: Hazard, elapsedMs: number): boolean {
  if (h.kind !== 'segment' || !h.blinkPeriodMs) return true;
  const phase = elapsedMs - (h.bornMs + h.telegraphMs);
  if (phase < 0) return true; // still telegraphing
  return phase % h.blinkPeriodMs < (h.blinkDutyOn ?? h.blinkPeriodMs);
}

/**
 * Is the player touching the mounted laser turret's solid BODY? The turret beam's
 * anchor `(x1,y1)` sits just off-screen; the body is mounted at that anchor clamped
 * {@link TURRET_BODY.pad}px in from the edge (matching the renderer), and is lethal
 * while the turret is firing — so dodging the beam but bumping the turret still ends
 * the run. Only meaningful for an active `turret` segment.
 */
function turretBodyHitsPlayer(h: Hazard, world: WorldState): boolean {
  if (h.kind !== 'segment' || h.family !== 'turret') return false;
  const pad = TURRET_BODY.pad;
  const bx = clamp(h.x1, pad, Math.max(pad, world.viewport.width - pad));
  const by = clamp(h.y1, pad, Math.max(pad, world.viewport.height - pad));
  return circleIntersectsCircle(
    world.playerX,
    world.playerY,
    world.playerRadius,
    bx,
    by,
    TURRET_BODY.radius,
  );
}

/**
 * Is the player touching the caro's spinning quad-turret HUB at its centre? Lethal
 * while the carousel is live, so the centre is a no-go zone. Only meaningful for an
 * active `caro` rotatingSegment.
 */
function caroHubHitsPlayer(h: Hazard, world: WorldState): boolean {
  if (h.kind !== 'rotatingSegment' || h.family !== 'caro') return false;
  const radius = Math.max(CARO.hubMinRadius, h.length * CARO.hubRadiusFraction);
  return circleIntersectsCircle(world.playerX, world.playerY, world.playerRadius, h.cx, h.cy, radius);
}

/** Has this hazard outlived its active window (ready to recycle)? */
function isExpired(h: Hazard, elapsedMs: number): boolean {
  return elapsedMs >= h.bornMs + h.telegraphMs + h.activeMs;
}

/**
 * Advance one hazard's live geometry by `dtMs`, mutating it in place. Motion is a
 * pure function of `dtMs` (never wall-clock) so it reproduces exactly:
 *  - `rect`            eases its top-left toward `targetX/targetY` (the wall crush)
 *                      using the same {@link PLAYER.followLerp}-style factor; a
 *                      rect without a target is static.
 *  - `segment`         is static during its active window (laser walls fly in
 *                      during the telegraph; the descriptor already holds the
 *                      final beam endpoints).
 *  - `rotatingSegment` advances `angle0` by `angVel * dt` (the caro spin).
 *  - `circleMover`     travels from `from` toward `to` at `speed`, stopping once
 *                      it arrives so it can't overshoot into noise.
 */
function advanceHazard(h: Hazard, dtMs: number, world: WorldState): void {
  const dtSec = dtMs / 1000;
  switch (h.kind) {
    case 'rect': {
      // Half-screen crusher: drive the chosen axis from the creep→crush→open keyframes.
      if (h.crushAxis && h.restPos !== undefined && h.creepPos !== undefined && h.crushPos !== undefined) {
        const pos = crusherPos(h, world.elapsedMs);
        if (h.crushAxis === 'x') h.x = pos;
        else h.y = pos;
      } else if (h.targetX !== undefined && h.targetY !== undefined) {
        // Legacy ease-toward-target motion (no crusher keyframes).
        const k = followFactor(dtMs);
        h.x += (h.targetX - h.x) * k;
        h.y += (h.targetY - h.y) * k;
      }
      return;
    }
    case 'segment': {
      // Generic drift: translate both endpoints (e.g. a sweeping laser wall).
      if (h.vx) {
        h.x1 += h.vx * dtSec;
        h.x2 += h.vx * dtSec;
      }
      if (h.vy) {
        h.y1 += h.vy * dtSec;
        h.y2 += h.vy * dtSec;
      }
      // Lock-on: re-aim the far endpoint at the player while TRACKING (the first
      // part of the telegraph). At `bornMs + aimLockMs` the aim FREEZES — the rest
      // of the telegraph is the CHARGE window (still harmless) during which the
      // player can dodge OFF the now-locked line before the beam fires. `aimLockMs`
      // defaults to `telegraphMs` (lock at telegraph end = legacy no-charge behaviour).
      if (h.aimPlayer) {
        // Clamp into [0, telegraphMs] so tracking can NEVER bleed into the lethal
        // window (an undodgeable lock-on) even if a future pattern sets it wrong.
        const lockMs = Math.min(h.telegraphMs, Math.max(0, h.aimLockMs ?? h.telegraphMs));
        if (world.elapsedMs < h.bornMs + lockMs) {
          const dx = world.playerX - h.x1;
          const dy = world.playerY - h.y1;
          const len = Math.hypot(dx, dy) || 1;
          const reach = h.aimReach ?? Math.hypot(world.viewport.width, world.viewport.height);
          h.x2 = h.x1 + (dx / len) * reach;
          h.y2 = h.y1 + (dy / len) * reach;
        }
      }
      return;
    }
    case 'rotatingSegment': {
      h.angle0 += h.angVel * dtSec;
      // Keep the angle bounded so long runs don't accumulate float drift.
      if (h.angle0 >= TWO_PI) h.angle0 -= TWO_PI * Math.floor(h.angle0 / TWO_PI);
      else if (h.angle0 < 0) h.angle0 += TWO_PI * Math.ceil(-h.angle0 / TWO_PI);
      return;
    }
    case 'circleMover': {
      const dx = h.to.x - h.from.x;
      const dy = h.to.y - h.from.y;
      const len = Math.hypot(dx, dy);
      if (len === 0) return;
      const travel = h.speed * dtSec;
      // Remaining distance to the destination from the current centre.
      const remX = h.to.x - h.x;
      const remY = h.to.y - h.y;
      const rem = Math.hypot(remX, remY);
      if (travel >= rem) {
        h.x = h.to.x;
        h.y = h.to.y;
      } else {
        h.x += (dx / len) * travel;
        h.y += (dy / len) * travel;
      }
      return;
    }
  }
}

/**
 * Does this hazard's lethal geometry overlap the player ball? Pure geometry,
 * narrowing on `hazard.kind` (rect / segment / rotatingSegment / circleMover) and
 * dispatching to the helpers in {@link import('./collisionGeo')}. A
 * `rotatingSegment` tests each of its evenly distributed beams. Callers must only
 * pass hazards that are currently in their active window.
 *
 * @param hazard the hazard to test.
 * @param px     player centre x (px).
 * @param py     player centre y (px).
 * @param r      player hit radius (px).
 * @returns true on contact.
 */
export function hazardHitsPlayer(hazard: Hazard, px: number, py: number, r: number): boolean {
  switch (hazard.kind) {
    case 'rect':
      return circleIntersectsRect(px, py, r, hazard.x, hazard.y, hazard.w, hazard.h);
    case 'segment':
      return circleIntersectsSegment(
        px,
        py,
        r,
        hazard.x1,
        hazard.y1,
        hazard.x2,
        hazard.y2,
        hazard.thickness,
      );
    case 'rotatingSegment': {
      // Beams are evenly distributed around the hub; beam i sits at
      // angle0 + i * (τ / beams). Any beam touching the player is a hit.
      const step = TWO_PI / hazard.beams;
      for (let i = 0; i < hazard.beams; i++) {
        const angle = hazard.angle0 + i * step;
        if (
          circleIntersectsRotatedBeam(
            px,
            py,
            r,
            hazard.cx,
            hazard.cy,
            angle,
            hazard.length,
            hazard.thickness,
          )
        ) {
          return true;
        }
      }
      return false;
    }
    case 'circleMover':
      return circleIntersectsCircle(px, py, r, hazard.x, hazard.y, hazard.r);
  }
}

/**
 * Advance the world by one fixed step, MUTATING `world` in place. The caller
 * supplies a constant `dtMs` (always {@link SIM.fixedDeltaMs} in production) so
 * behaviour is frame-rate independent and reproducible.
 *
 * Per step it does, in order:
 *  1. ease the player toward `input` via {@link PLAYER.followLerp} and clamp to
 *     the viewport;
 *  2. when `elapsedMs` has reached `nextAttackAtMs`, schedule the next attack via
 *     {@link selectPattern} (using {@link difficultyForElapsed}) — appending its
 *     hazards, updating `lastFamily`, and pushing `nextAttackAtMs` out by the
 *     tier's `spacingMs`;
 *  3. advance live hazards (move/rotate) and drop any past their active window;
 *  4. test collision (only against *active* hazards); on a hit set `alive=false`
 *     and `deathFamily` and stop;
 *  5. advance `elapsedMs` by `dtMs`.
 *
 * No-op once `world.alive` is false.
 *
 * @param world the world to mutate.
 * @param dtMs  the fixed step length (ms).
 * @param input the player's pointer target for this step (viewport px).
 */
export function stepWorld(world: WorldState, dtMs: number, input: PlayerInput): void {
  if (!world.alive) return;

  // 1. Player easing toward the pointer target, then clamp inside the viewport.
  const k = followFactor(dtMs);
  world.playerX += (input.x - world.playerX) * k;
  world.playerY += (input.y - world.playerY) * k;
  world.playerX = clamp(world.playerX, 0, world.viewport.width);
  world.playerY = clamp(world.playerY, 0, world.viewport.height);

  // 2. Schedule due attacks. A `while` (not `if`) drains any backlog should a
  // single tier's spacing ever fall below `dtMs`, keeping cadence deterministic.
  while (world.elapsedMs >= world.nextAttackAtMs) {
    const difficulty = difficultyForElapsed(world.elapsedMs);
    const pattern = selectPattern(world.rng, difficulty, world.lastFamily);
    const spawned = pattern.spawn({
      rng: world.rng,
      difficulty,
      viewport: world.viewport,
      elapsedMs: world.elapsedMs,
    });
    // Stamp each spawned hazard with a unique, deterministic id (spawn order) so
    // the renderer can reconcile sprites by id — patterns emit id:0 as a placeholder.
    // Track when this whole attack finishes (its latest-expiring hazard).
    let attackEndsMs = world.elapsedMs;
    for (const hazard of spawned) {
      // Guard: a zero-width active window can never register a collision (the
      // active range [start, start) is empty). Ensure every lethal hazard is active
      // for at least one step — defends against a future pattern emitting activeMs<=0.
      if (hazard.activeMs <= 0) hazard.activeMs = dtMs;
      hazard.id = world.nextHazardId++;
      world.hazards.push(hazard);
      const expiry = hazard.bornMs + hazard.telegraphMs + hazard.activeMs;
      if (expiry > attackEndsMs) attackEndsMs = expiry;
    }
    world.lastFamily = pattern.family;
    world.nextAttackAtMs += difficulty.spacingMs;
    // Early game (below the 3rd speed-up): keep to ONE attack at a time so the field
    // reads clearly — don't start the next until the current attack has fully cleared
    // (plus a calm beat). From SOLO_ATTACK_BELOW_STEP on, attacks may overlap.
    if (speedStepForElapsed(world.elapsedMs) < SOLO_ATTACK_BELOW_STEP) {
      const earliestNext = attackEndsMs + ATTACK_GAP_MS;
      if (world.nextAttackAtMs < earliestNext) world.nextAttackAtMs = earliestNext;
    }
  }

  // 3. Advance + expire hazards. Compact in place (no per-step allocation churn
  // on the survivors) while dropping any that have outlived their active window.
  let write = 0;
  for (let read = 0; read < world.hazards.length; read++) {
    const h = world.hazards[read]!;
    advanceHazard(h, dtMs, world);
    if (!isExpired(h, world.elapsedMs)) {
      world.hazards[write++] = h;
    }
  }
  world.hazards.length = write;

  // 4. Collision — only hazards inside their lethal window can kill. A blinking
  // sweep laser is additionally harmless during its OFF beats (the dash window).
  // For a turret/caro, the solid emitter itself (turret body / caro hub) is lethal
  // too, not just the beam.
  for (const h of world.hazards) {
    if (!isActive(h, world.elapsedMs)) continue;
    if (!segmentBlinkOn(h, world.elapsedMs)) continue;
    const hit =
      hazardHitsPlayer(h, world.playerX, world.playerY, world.playerRadius) ||
      turretBodyHitsPlayer(h, world) ||
      caroHubHitsPlayer(h, world);
    if (hit) {
      world.alive = false;
      world.deathFamily = h.family;
      return; // Dead — do not advance the clock past the lethal moment.
    }
  }

  // 5. Advance the survival clock (only reached while still alive).
  world.elapsedMs += dtMs;
}

/**
 * Advance ONLY the hazards' visuals for one cosmetic post-death step — no attack
 * scheduling, no collision, no scoring. The authoritative run has already ended (the
 * score is locked at the death moment), so this is purely for the renderer to play
 * the in-flight attacks out to completion before the game-over screen appears. NOT
 * part of the deterministic outcome and never run during the server re-simulation.
 *
 * @param world the (already-dead) world to animate forward.
 * @param dtMs  the fixed step length (ms).
 */
export function advanceHazardsCosmetic(world: WorldState, dtMs: number): void {
  let write = 0;
  for (let read = 0; read < world.hazards.length; read++) {
    const h = world.hazards[read]!;
    advanceHazard(h, dtMs, world);
    if (!isExpired(h, world.elapsedMs)) {
      world.hazards[write++] = h;
    }
  }
  world.hazards.length = write;
  world.elapsedMs += dtMs;
}

/** The authoritative outcome of re-running a {@link RunRecord}. */
export interface RunOutcome {
  /** Survival score via {@link scoreFromElapsedMs}. */
  score: number;
  /** Which family killed the player, or null if the record never ends in death. */
  deathFamily: WorldState['deathFamily'];
  /** Total survival time (ms). */
  durationMs: number;
  /**
   * Player position at the death frame (viewport px), null when the record never
   * ends in death. REPORTED OUTPUT ONLY — reading the world's final position does
   * not alter sim behavior, so this is not a SIM_VERSION concern. Feeds the
   * death-location heat aggregates.
   */
  deathX: number | null;
  deathY: number | null;
}

/**
 * Resolve the player's pointer target at time `tMs` from the recorded samples by
 * linear interpolation. Samples are assumed sorted ascending by `tMs` (the
 * recorder appends in order). Before the first / after the last sample the
 * nearest sample is held (no extrapolation). An empty timeline reports the
 * viewport centre so the player simply sits still.
 *
 * Pure and allocation-light — called once per fixed step during re-simulation.
 */
function inputAt(samples: readonly RunInputSample[], tMs: number, viewport: Viewport): PlayerInput {
  const n = samples.length;
  if (n === 0) return { x: viewport.width / 2, y: viewport.height / 2 };

  const first = samples[0]!;
  if (tMs <= first.tMs) return { x: first.x, y: first.y };

  const last = samples[n - 1]!;
  if (tMs >= last.tMs) return { x: last.x, y: last.y };

  // Linear scan for the bracketing pair. Sample counts are small (a run is a few
  // KB) so a scan beats a binary search's branch overhead and stays simple.
  for (let i = 1; i < n; i++) {
    const b = samples[i]!;
    if (tMs <= b.tMs) {
      const a = samples[i - 1]!;
      const span = b.tMs - a.tMs;
      // Coincident timestamps: fall back to the later sample (no divide-by-zero).
      if (span <= 0) return { x: b.x, y: b.y };
      const f = (tMs - a.tMs) / span;
      return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
    }
  }
  // Unreachable (tMs < last.tMs guaranteed above), but keeps the function total.
  return { x: last.x, y: last.y };
}

/**
 * Re-run a recorded run headlessly and return its authoritative outcome. This is
 * the anti-cheat / replay entry point: it rebuilds the world from `record.seed` +
 * `record.viewport` + `record.mode`, then drives {@link stepWorld} at a fixed
 * {@link SIM.fixedDeltaMs} cadence, resolving each step's {@link PlayerInput} from
 * `record.samples` (interpolated to the step boundary), until the player dies or
 * the input timeline runs out. Score is computed from the final `elapsedMs` via
 * {@link scoreFromElapsedMs}.
 *
 * Pure and deterministic — the server runs exactly this to validate a submission.
 *
 * @param record the replayable run.
 * @returns the score, death family, and duration the sim produces.
 */
export function runSimulation(record: RunRecord): RunOutcome {
  const world = createWorld(record.seed, record.viewport, record.mode);
  const dt = SIM.fixedDeltaMs;
  const samples = record.samples;
  // The run STARTS at the player's first sampled position (the drag/pointer spot at
  // go-time), not dead centre — so there's no snap-to-centre. The live client seeds
  // the same start, keeping the re-simulation in lock-step.
  if (samples.length > 0) {
    const start = inputAt(samples, 0, record.viewport);
    world.playerX = start.x;
    world.playerY = start.y;
  }
  // Stop once we've consumed the recorded inputs: the last sample's timestamp is
  // the run's wall length. (Death may end it sooner.)
  const endMs = samples.length > 0 ? samples[samples.length - 1]!.tMs : 0;

  // Advance step-by-step. `elapsedMs` is the time at the START of the step; the
  // input is sampled at that boundary, matching the live recorder's cadence.
  while (world.alive && world.elapsedMs < endMs) {
    const input = inputAt(samples, world.elapsedMs, world.viewport);
    stepWorld(world, dt, input);
  }

  return {
    score: scoreFromElapsedMs(world.elapsedMs),
    deathFamily: world.deathFamily,
    durationMs: world.elapsedMs,
    deathX: world.alive ? null : world.playerX,
    deathY: world.alive ? null : world.playerY,
  };
}
